import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation } from 'swiper';

import Checkbox from '@/components/Checkbox';
import { colors } from '@/constants/theme';
import ArrowIcon from '@/assets/svg/detailed-car/arrow.svg';
import { CarCard } from '@/components/CarModelCompareLandingWidgets';
import {
  useCarModelCompareLandingContext,
  useCarModelCompareLandingData,
} from '@/modules/CarModelCompareLanding/hooks';
import { ContentContainer } from '@/modules/CarModelCompareLanding/common.styles';
import { useFixedElement } from '@/hooks/useFixedElement';

SwiperCore.use([Navigation]);

function ModelsDesktop() {
  const { models } = useCarModelCompareLandingData();
  const { isDifferentParams, setDifferentParams, setCurrentSlideIndex } =
    useCarModelCompareLandingContext();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const isFixed = useFixedElement(componentRef);

  if (!models || models.length === 0) {
    return null;
  }

  const slidesPerView = 5;
  const isSliderOptions = models.length > slidesPerView;

  return (
    <Component ref={componentRef} smallHeader={isFixed}>
      <ContentContainer>
        <Inner>
          <Aside>
            <Checkbox
              label="Скрыть одинаковые параметры"
              checked={isDifferentParams}
              onChange={setDifferentParams}
            />
          </Aside>
          <CarsList>
            <Swiper
              slidesPerView={slidesPerView}
              spaceBetween={10}
              onSlideChange={(swiper) => {
                setCurrentSlideIndex(swiper.activeIndex);
              }}
              navigation={{
                prevEl: `.swiper-prev`,
                nextEl: `.swiper-next`,
              }}
              noSwipingClass="swiper-no-swiping"
            >
              {models.map(({ name, price, image, link, isNewTab }, index) => (
                <SwiperSlide
                  key={index}
                  className={!isSliderOptions ? 'swiper-no-swiping' : ''}
                >
                  <CarCard
                    image={image}
                    name={name ?? ''}
                    price={price ?? ''}
                    link={link ?? ''}
                    isNewTab={isNewTab}
                    isMobile={isFixed}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {isSliderOptions && (
              <NavButtons>
                <NavButton className="swiper-prev" prev>
                  <ArrowIcon />
                </NavButton>
                <NavButton className="swiper-next" next>
                  <ArrowIcon />
                </NavButton>
              </NavButtons>
            )}
          </CarsList>
        </Inner>
      </ContentContainer>
    </Component>
  );
}

export default ModelsDesktop;

const Component = styled.div<{ smallHeader: boolean }>`
  ${({ smallHeader }) =>
    smallHeader &&
    css`
      padding: 40px 0;
      box-shadow: ${(props) => props.theme.blackShadow100};
      background: ${(props) => props.theme.white_1};
    `}
`;

const Inner = styled.div`
  display: flex;
  max-width: 100%;
`;

const Aside = styled.div`
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 310px;
  flex: 0 0 310px;
`;

const CarsList = styled.div`
  flex: 1;
  max-width: calc(100% - 310px);
  padding: 0 50px;
  position: relative;

  .swiper-container {
    overflow: hidden;
    height: 100%;
  }
`;

const NavButtons = styled.div`
  z-index: 5;
  width: 100%;
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 0;
`;

const NavButton = styled.button<{ prev?: boolean; next?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0.7px solid ${colors.gray400};
  overflow: hidden;

  svg path {
    fill: ${colors.main};
  }

  &.swiper-button-disabled {
    pointer-events: none;

    svg path {
      fill: #dcdcdc !important;
    }
  }

  svg {
    width: 9px;
    height: 16px;
  }

  ${(props) =>
    props.prev
      ? css`
          svg {
            transform: rotate(-180deg);
          }
        `
      : css``}
`;
